import { Box, Chip } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import {
    Collections,
    Infraction,
    PUBLICS_CHAMPIONSHIPS_STORE_AS,
    Season,
    StatusProtest,
    StatusProtestColor,
    StatusProtestDescription,
    useCollection,
    useInvolvedProtests,
    usePublicsChampionships,
} from "api";
import { DateTime } from "luxon";
import MaterialTable, { MTableToolbar } from "material-table";
import React, { useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { RootState } from "redux/model";
import {
    AddButton,
    BoxCard,
    LoggedUserContext,
    PageLayout,
} from "ui/componentes";
import { ProtestAnswerForm } from "ui/views/Protests/ProtestAnswerForm";
import { ProtestFormDialog } from "ui/views/Protests/ProtestFormDialog";
import { LocalizationPTBr } from "util/MaterialTableUtil";
import { Add as AddIcon } from "@material-ui/icons";
import { useDriversByChampById } from "ui/views/Protests/hooks/useDriversByChampById";

export const ProtestDefaultValues = {
    seasonId: "",
    authorDriverId: "",
    championshipId: "",
    roundId: "",
    raceControlId: "",
    lap: "",
    infractionId: "",
    complaint: "",
    protestedDriverId: "",
    createdAt: "",
    defense: "",
    defenseAt: "",
    status: StatusProtest.OPEN,
};

const ProtestView = () => {
    const { id } = useParams<{ id }>();
    const [openProtestForm, setOpenProtestForm] = useState(false);
    const [openDefenseForm, setOpenDefenseForm] = useState(!!id);
    const [protestIdToDefense, setProtestIdToDefense] = useState("");
    const loggedUser = useContext(LoggedUserContext);

    const [selectedDate, handleDateChange] = useState<DateTime | null>(
        DateTime.local().minus({ day: 7 }).startOf("day")
    );

    const protests = useInvolvedProtests({
        userId: loggedUser.id || "",
        initialDate: selectedDate?.toFormat("yyyy-MM-dd HH:mm:ss"),
    });

    const championshipsId = useMemo(() => {
        if (protests && protests.length > 0) {
            return Array.from(
                new Set(protests.map((protest) => protest.championshipId))
            );
        }
        return ["-1"];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [protests.length]);

    const seasonById =
        useCollection<object>({
            collection: Collections.SEASONS,
            orderBy: ["endAt", "asc"],
        }) || {};

    usePublicsChampionships();
    const championshipsById =
        useSelector(
            (state: RootState) =>
                state.firestore.data[PUBLICS_CHAMPIONSHIPS_STORE_AS]
        ) || {};

    const roundsById =
        useCollection<object>({
            collection: Collections.ROUNDS,
            where: ["championshipId", "in", championshipsId],
            storeAs: "roundsToProtests",
        }) || {};

    const driversByChampById = useDriversByChampById(championshipsId);

    const infractions =
        useCollection<object>({ collection: Collections.INFRACTIONS }) || {};

    return (
        <PageLayout title="" showInBoxCard={false}>
            <ProtestFormDialog
                open={openProtestForm}
                onClose={() => setOpenProtestForm(false)}
            />
            <ProtestAnswerForm
                protestId={id || protestIdToDefense}
                open={openDefenseForm}
                onClose={() => {
                    setOpenDefenseForm(false);
                    setProtestIdToDefense("");
                }}
            />
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                width="100%"
            >
                <MaterialTable
                    components={{
                        Container: BoxCard,
                        Toolbar: (props) => (
                            <div>
                                <MTableToolbar {...props} />
                                <KeyboardDatePicker
                                    fullWidth={false}
                                    label="A partir de"
                                    value={selectedDate}
                                    onChange={(date) => handleDateChange(date)}
                                    format="dd/MM/yyyy"
                                    style={{ marginBottom: 30 }}
                                />
                            </div>
                        ),
                    }}
                    title={`Seus protestos`}
                    data={protests}
                    columns={[
                        {
                            field: "status",
                            title: "Status",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                            render: (protest) => {
                                if (!protest.status) return "";
                                return (
                                    <Chip
                                        label={
                                            StatusProtestDescription[
                                                protest.status
                                            ]
                                        }
                                        style={
                                            StatusProtestColor[protest.status]
                                        }
                                        size="small"
                                    />
                                );
                            },
                        },
                        {
                            field: "authorDriverId",
                            title: "Piloto protestante",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                            render: (protest) => {
                                const driversById =
                                    driversByChampById[protest.championshipId];
                                if (driversById) {
                                    return driversById[protest.authorDriverId];
                                }
                                return "";
                            },
                        },
                        {
                            field: "protestedDriverId",
                            title: "Piloto protestado",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                            render: (protest) => {
                                const driversById =
                                    driversByChampById[protest.championshipId];
                                if (driversById) {
                                    return driversById[
                                        protest.protestedDriverId
                                    ];
                                }
                                return "";
                            },
                        },
                        {
                            field: "seasonId",
                            title: "Temporada",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                            render: (protest) => {
                                const season = seasonById[
                                    protest.seasonId
                                ] as Season;

                                if (!season) return "";

                                return `${season.year} ${season.name} ${
                                    season.current ? "- atual" : ""
                                }`;
                            },
                        },
                        {
                            field: "championshipId",
                            title: "Campenato",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                            render: (protest) => {
                                return championshipsById[protest.championshipId]
                                    ?.name;
                            },
                        },
                        {
                            field: "roundId",
                            title: "Etapa",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                            render: (protest) => {
                                return roundsById[protest.roundId]?.trackName;
                            },
                        },
                        {
                            field: "raceNumber",
                            title: "Bateria",
                            align: "center",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                        },
                        {
                            field: "lap",
                            title: "Volta",
                            width: "5%",
                            align: "center",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                        },
                        {
                            field: "infractionId",
                            title: "Infração",
                            width: "30%",
                            align: "center",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                            render: (protest) => {
                                const infraction = infractions[
                                    protest.infractionId
                                ] as Infraction;

                                return infraction
                                    ? `${infraction.key} - ${infraction.description}`
                                    : "";
                            },
                        },
                        {
                            field: "defense",
                            title: "Houve defesa?",
                            width: "7%",
                            align: "center",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                            render: (protest) => {
                                return protest.defense ? "Sim" : "Não";
                            },
                        },
                        {
                            field: "createdAt",
                            title: "Criado em",
                            cellStyle: () => ({
                                padding: "6px 8px 6px 8px",
                            }),
                            render: (protest) => {
                                return DateTime.fromFormat(
                                    protest.createdAt,
                                    "yyyy-MM-dd HH:mm:ss"
                                ).toFormat("dd/MM/yyyy HH:mm:ss");
                            },
                        },
                    ]}
                    localization={LocalizationPTBr}
                    options={{
                        draggable: false,
                        sorting: false,
                        search: false,
                        pageSize: 10,
                        rowStyle: { fontSize: "0.8rem", padding: 0 },
                    }}
                    actions={[
                        (protest) => {
                            return {
                                icon:
                                    protest.protestedUserId === loggedUser.id &&
                                    !protest.defense
                                        ? "edit"
                                        : "search",
                                onClick: () => {
                                    setOpenDefenseForm(true);
                                    setProtestIdToDefense(protest.id);
                                },
                            };
                        },
                    ]}
                />
            </Box>

            <AddButton onClick={() => setOpenProtestForm(true)}>
                <AddIcon />
            </AddButton>
        </PageLayout>
    );
};

export { ProtestView };
